import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { SideNaveActionsTypes, SideNaveData, SideNaveResponseData } from '@klickdata/core/mobile/src/mobile.service';
import { AppScope, Resource, ResourceData, ResourceService, ResourceTypes } from '@klickdata/core/resource';
import { VTQService } from '@klickdata/core/vtq';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { first, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-menu-side-video-to-quiz',
    templateUrl: './menu-side-video-to-quiz.component.html',
    styleUrls: ['./menu-side-video-to-quiz.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuSideVideoToQuizComponent implements OnDestroy, OnChanges {
    @Input() public navData: SideNaveData;
    @Output() onClose: EventEmitter<SideNaveResponseData> = new EventEmitter();
    public destroy: Subject<boolean> = new Subject<boolean>();
    public loading: Subject<boolean> = new Subject<boolean>();
    SideNaveActionsTypes = SideNaveActionsTypes;
    public form: FormGroup;
    AppScope = AppScope;
    public relatedTests: BehaviorSubject<ResourceData[]> = new BehaviorSubject<ResourceData[]>([]);
    public customer$: Observable<Customer>;
    constructor(
        protected fb: FormBuilder,
        protected vTQService: VTQService,
        protected auth: AuthService,
        protected messageService: MessageService,
        protected router: Router,
        protected resourceService: ResourceService
    ) {
        this.customer$ = this.auth.getCustomer();
        this.form = fb.group({
            question_count: [10, Validators.required],
            difficulty_level: [3, Validators.required],
            create_course: [false],
            question_type_value: [],
            create_quiz: [],
        });
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (this.navData?.data?.resource?.related_resources?.length) {
            this.relatedTests.next(
                this.navData.data.resource.related_resources.filter((res) => res.type_id === ResourceTypes.GeneralTest)
            );
        }
    }
    get resource() {
        return this.navData.data.resource;
    }
    get resourceItemIds(): number[] {
        return this.navData.data.itemIds;
    }

    get materialLabel$(): Observable<string> {
        return this.customer$.pipe(
            map((customer) => customer.scope_names[AppScope.MATERIAL] ?? AppScope.label(AppScope.MATERIAL))
        );
    }
    get testLabel$(): Observable<string> {
        return this.customer$.pipe(
            map((customer) => customer.scope_names[AppScope.TEST] ?? AppScope.label(AppScope.TEST))
        );
    }
    updateForm(ev: {
        question_count: number;
        difficulty_level: number;
        create_course: boolean;
        question_type_value: any;
        create_quiz: boolean;
    }) {
        this.form.patchValue({
            question_count: ev.question_count,
            difficulty_level: ev.difficulty_level,
            create_course: ev.create_course,
            question_type_value: ev.question_type_value,
            create_quiz: ev.create_quiz,
        });
    }
    onTestClick(id: number) {
        this.onClose.emit();
        this.router.navigate(['/home/dashboard/resource-details/' + id]);
    }
    submitForm() {
        this.loading.next(true);
        const createQuizObs = this.form.value.create_quiz ? this.createTest() : this.createCourse();
        createQuizObs.pipe(takeUntil(this.destroy)).subscribe(
            () => {
                this.loading.next(false);
                this.messageService.openMessage(MessageSavedComponent, $localize`Done.`);
                this.vTQService.setActiveVTQ(null);
                this.onClose.emit({ action: SideNaveActionsTypes.POSITIVE, clearCompData: true });
            },
            (err) => {
                this.loading.next(false);
                this.vTQService.setActiveVTQ(null);
                this.onClose.emit();
                this.messageService.openMessage(MessageErrorComponent, $localize`An unknown error occurred.`);
            }
        );
    }
    private createCourse(): Observable<any> {
        return this.resourceService.store({
            title: this.resource.title,
            type_id: ResourceTypes.GeneralCoursePlan,
            resource_items: [{ child_resource_id: this.resource.id }],
        });
    }
    private createTest(): Observable<any> {
        const data: any = {
            material_id: this.resource.id,
            question_count: this.form.value.question_count,
            difficulty_level: this.form.value.difficulty_level,
            create_course: this.form.value.create_course,
            question_type_value: this.form.value.question_type_value,
        };
        if (!!this.resourceItemIds?.length) {
            data.item_ids = this.resourceItemIds;
        }
        return this.vTQService.create(data);
    }

    public ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
